import React, { useState, useEffect, memo, useMemo } from 'react';
import { Divider, Checkbox, Dropdown, Menu, Space, Button, message, Tooltip } from 'antd';
import { CaretDownOutlined, MoreOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { StyledNavBar, StyledNavLeft, StyledNavRight, StyledInfoIcon } from './styled';
import { IAnnotation } from './index';
import { useDeleteAnnotation, useUpdateAnnotation } from '../hooks/useMutateAnnotation';
import { tid } from '@utils/test';

const annotationSaveOptions = [
  {
    label: 'This metric',
    key: 'metric',
  },
  {
    label: 'All metrics',
    key: 'all',
  },
];

const kebabOptions = [
  {
    label: 'Delete',
    key: 'delete',
  },
];

const getHelpText = (selectedKey) => {
  switch (selectedKey) {
    case 'metric':
      return 'Annotations created will be visible for this metric across all Dashboards';
    case 'all':
      return 'Annotations created will be visible for all metrics across all Dashboards';
    default:
      return '';
  }
};

const NavBar: React.FC<{
  activeAnnotation: IAnnotation;
  profile: any;
  updateActiveAnnotation: (any) => void;
  metricKey: string;
}> = ({ activeAnnotation, profile, updateActiveAnnotation, metricKey, setOpen }) => {
  const { annotationType, isPublic, annotationId, createdBy } = activeAnnotation;
  const { profile_id } = profile;

  const [selectedKey, setSelectedKey] = useState('metric');
  const [selected, setSelected] = useState<IOptions | undefined>({});

  const [checked, setChecked] = useState(false);
  const [updateStatus, setUpdateStatus] = useState(false);

  const updateAnnotation = useUpdateAnnotation();
  const deleteAnnotation = useDeleteAnnotation();

  const readOnly = useMemo(() => profile_id !== createdBy, [profile_id, createdBy]);

  useEffect(() => {
    if (annotationId && (selectedKey !== annotationType || isPublic !== checked)) {
      setUpdateStatus(true);
    } else {
      setUpdateStatus(false);
    }
  }, [selectedKey, isPublic, annotationType, checked, annotationId]);

  const handleUpdate = () => {
    updateAnnotation.mutate(
      {
        annotationId,
        annotationType: selectedKey,
        isPublic: checked,
        metricKey,
      },
      {
        onSuccess: () => {
          message.success('Annotation updated successfully.');
        },
      },
    );
  };

  const onAnnotationTypeChange = (event) => {
    setChecked(event.target.checked);
    if (!annotationId) {
      updateActiveAnnotation({
        isPublic: event.target.checked,
      });
    }
  };

  const onAnnotationLocationChange = (event) => {
    setSelectedKey(event.key);
    const selected = annotationSaveOptions.find((item) => item.key === event.key);
    if (selected) {
      setSelected(selected);
    }
    if (!annotationId) {
      updateActiveAnnotation({
        annotationType: event.key,
      });
    }
  };

  useEffect(() => {
    const selected = annotationSaveOptions.find((item) => item.key === annotationType);
    setSelectedKey(annotationType);
    setSelected(selected);
  }, [annotationType]);

  useEffect(() => {
    setChecked(isPublic);
  }, [isPublic]);

  const onApply = (e) => {
    switch (e.key) {
      case 'delete': {
        deleteAnnotation.mutate(annotationId, {
          onSuccess: () => {
            setOpen(false);
            message.success('Annotation deleted successfully.');
          },
        });
        break;
      }
    }
  };

  return (
    <StyledNavBar>
      <StyledNavLeft>
        <Checkbox checked={checked} onChange={onAnnotationTypeChange} disabled={readOnly}>
          {' '}
          Public Annotation
        </Checkbox>
        <Divider type="vertical" />
        <Dropdown
          menu={{
            items: annotationSaveOptions,
            onClick: onAnnotationLocationChange,
          }}
          trigger={['click']}
          disabled={readOnly}
        >
          <a onClick={(e) => e.preventDefault()}>
            <Space>
              {selected ? selected.label : ''}
              <CaretDownOutlined />
            </Space>
          </a>
        </Dropdown>
        <Divider type="vertical" />
        <Tooltip title={<div>{getHelpText(selectedKey)}</div>}>
          <StyledInfoIcon>
            <InfoCircleOutlined />
          </StyledInfoIcon>
        </Tooltip>
      </StyledNavLeft>
      <StyledNavRight>
        {updateStatus ? (
          <Button
            onClick={handleUpdate}
            size="small"
            type="primary"
            ghost
            {...tid('update-annotation-btn')}
          >
            Update
          </Button>
        ) : null}
        {!readOnly && annotationId ? (
          <Dropdown
            menu={{
              items: kebabOptions,
              onClick: onApply,
            }}
            trigger={['click']}
          >
            <Space>
              <MoreOutlined />
            </Space>
          </Dropdown>
        ) : null}
      </StyledNavRight>
    </StyledNavBar>
  );
};

export default memo(NavBar);
